import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Search, X, Loader2, User, Briefcase, FileText, Calendar, DollarSign, MessageSquare } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { cn, formatDate } from '../../lib/utils';

const SEARCH_CATEGORIES = [
  { id: 'all', label: 'All', icon: Search },
  { id: 'cases', label: 'Cases', icon: Briefcase },
  { id: 'clients', label: 'Clients', icon: User },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'tasks', label: 'Tasks', icon: Calendar },
  { id: 'invoices', label: 'Invoices', icon: DollarSign },
];

export function GlobalSearch({ open, onOpenChange }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({
    cases: [],
    clients: [],
    documents: [],
    tasks: [],
    invoices: [],
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Focus input when dialog opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setQuery('');
      setResults({ cases: [], clients: [], documents: [], tasks: [], invoices: [] });
      setSelectedIndex(0);
    }
  }, [open]);

  // Search debounce
  useEffect(() => {
    if (!query.trim()) {
      setResults({ cases: [], clients: [], documents: [], tasks: [], invoices: [] });
      return;
    }

    const timer = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, category]);

  const performSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const response = await api.get('/search/global', {
        params: { q: searchQuery, category: category === 'all' ? undefined : category }
      });
      setResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
      // Fallback to individual searches
      const searchResults = { cases: [], clients: [], documents: [], tasks: [], invoices: [] };
      
      try {
        if (category === 'all' || category === 'cases') {
          const casesRes = await api.get('/cases', { params: { search: searchQuery } });
          searchResults.cases = (casesRes.data || []).slice(0, 5);
        }
        if (category === 'all' || category === 'clients') {
          const clientsRes = await api.get('/clients', { params: { search: searchQuery } });
          searchResults.clients = (clientsRes.data || []).slice(0, 5);
        }
        if (category === 'all' || category === 'documents') {
          const docsRes = await api.get('/documents', { params: { search: searchQuery } });
          searchResults.documents = (docsRes.data || []).slice(0, 5);
        }
        if (category === 'all' || category === 'tasks') {
          const tasksRes = await api.get('/tasks', { params: { search: searchQuery } });
          searchResults.tasks = (tasksRes.data.tasks || tasksRes.data || []).slice(0, 5);
        }
        if (category === 'all' || category === 'invoices') {
          const invoicesRes = await api.get('/invoices', { params: { search: searchQuery } });
          searchResults.invoices = (invoicesRes.data || []).slice(0, 5);
        }
        setResults(searchResults);
      } catch (err) {
        console.error('Fallback search failed:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const getAllResults = () => {
    const all = [];
    
    results.cases?.forEach(item => all.push({ ...item, _type: 'case' }));
    results.clients?.forEach(item => all.push({ ...item, _type: 'client' }));
    results.documents?.forEach(item => all.push({ ...item, _type: 'document' }));
    results.tasks?.forEach(item => all.push({ ...item, _type: 'task' }));
    results.invoices?.forEach(item => all.push({ ...item, _type: 'invoice' }));
    
    return all;
  };

  const handleSelect = (item) => {
    onOpenChange(false);
    
    switch (item._type) {
      case 'case':
        navigate(`/cases/${item.case_id}`);
        break;
      case 'client':
        navigate(`/clients/${item.client_id}`);
        break;
      case 'document':
        navigate(`/documents`);
        break;
      case 'task':
        navigate(`/tasks`);
        break;
      case 'invoice':
        navigate(`/billing`);
        break;
      default:
        break;
    }
  };

  const allResults = getAllResults();

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, allResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && allResults[selectedIndex]) {
      e.preventDefault();
      handleSelect(allResults[selectedIndex]);
    }
  };

  const getResultIcon = (type) => {
    switch (type) {
      case 'case': return <Briefcase className="h-4 w-4 text-blue-500" />;
      case 'client': return <User className="h-4 w-4 text-green-500" />;
      case 'document': return <FileText className="h-4 w-4 text-purple-500" />;
      case 'task': return <Calendar className="h-4 w-4 text-orange-500" />;
      case 'invoice': return <DollarSign className="h-4 w-4 text-emerald-500" />;
      default: return <Search className="h-4 w-4" />;
    }
  };

  const getResultTitle = (item) => {
    switch (item._type) {
      case 'case': return item.title || item.case_number || 'Untitled Case';
      case 'client': return item.name || item.company || 'Unknown Client';
      case 'document': return item.name || item.filename || 'Untitled Document';
      case 'task': return item.title || item.description || 'Untitled Task';
      case 'invoice': return `Invoice #${item.invoice_number || item.invoice_id?.slice(-8)}`;
      default: return 'Unknown';
    }
  };

  const getResultSubtitle = (item) => {
    switch (item._type) {
      case 'case': return `${item.practice_area || 'General'} • ${item.status || 'Active'}`;
      case 'client': return item.email || item.phone || 'No contact info';
      case 'document': return formatDate(item.created_at);
      case 'task': return `Due: ${formatDate(item.due_date)}`;
      case 'invoice': return `$${(item.total || 0).toLocaleString()}`;
      default: return '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden" data-testid="global-search-dialog">
        <div className="flex items-center border-b px-4 py-3">
          <Search className="h-5 w-5 text-muted-foreground mr-3" />
          <Input
            ref={inputRef}
            placeholder="Search cases, clients, documents, tasks..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 border-0 focus-visible:ring-0 px-0 text-base"
            data-testid="global-search-input"
          />
          {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground ml-2" />}
          {query && !loading && (
            <button onClick={() => setQuery('')} className="ml-2">
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Category Filters */}
        <div className="flex gap-1 px-4 py-2 border-b bg-slate-50">
          {SEARCH_CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setCategory(cat.id)}
              className={cn(
                "px-3 py-1.5 text-sm rounded-full transition-colors flex items-center gap-1.5",
                category === cat.id
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:bg-slate-200"
              )}
            >
              <cat.icon className="h-3.5 w-3.5" />
              {cat.label}
            </button>
          ))}
        </div>

        {/* Results */}
        <ScrollArea className="max-h-[400px]">
          {!query ? (
            <div className="p-8 text-center text-muted-foreground">
              <Search className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p>Start typing to search across your workspace</p>
              <p className="text-sm mt-1">Press <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-xs">↵</kbd> to select</p>
            </div>
          ) : allResults.length === 0 && !loading ? (
            <div className="p-8 text-center text-muted-foreground">
              <Search className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p>No results found for "{query}"</p>
              <p className="text-sm mt-1">Try different keywords or check the category filter</p>
            </div>
          ) : (
            <div className="py-2">
              {/* Cases */}
              {results.cases?.length > 0 && (category === 'all' || category === 'cases') && (
                <div className="mb-2">
                  <div className="px-4 py-1.5 text-xs font-medium text-muted-foreground uppercase">Cases</div>
                  {results.cases.map((item, idx) => {
                    const globalIdx = allResults.findIndex(r => r._type === 'case' && r.case_id === item.case_id);
                    return (
                      <ResultItem
                        key={item.case_id}
                        item={{ ...item, _type: 'case' }}
                        selected={selectedIndex === globalIdx}
                        onClick={() => handleSelect({ ...item, _type: 'case' })}
                        icon={getResultIcon('case')}
                        title={getResultTitle({ ...item, _type: 'case' })}
                        subtitle={getResultSubtitle({ ...item, _type: 'case' })}
                      />
                    );
                  })}
                </div>
              )}

              {/* Clients */}
              {results.clients?.length > 0 && (category === 'all' || category === 'clients') && (
                <div className="mb-2">
                  <div className="px-4 py-1.5 text-xs font-medium text-muted-foreground uppercase">Clients</div>
                  {results.clients.map((item, idx) => {
                    const globalIdx = allResults.findIndex(r => r._type === 'client' && r.client_id === item.client_id);
                    return (
                      <ResultItem
                        key={item.client_id}
                        item={{ ...item, _type: 'client' }}
                        selected={selectedIndex === globalIdx}
                        onClick={() => handleSelect({ ...item, _type: 'client' })}
                        icon={getResultIcon('client')}
                        title={getResultTitle({ ...item, _type: 'client' })}
                        subtitle={getResultSubtitle({ ...item, _type: 'client' })}
                      />
                    );
                  })}
                </div>
              )}

              {/* Documents */}
              {results.documents?.length > 0 && (category === 'all' || category === 'documents') && (
                <div className="mb-2">
                  <div className="px-4 py-1.5 text-xs font-medium text-muted-foreground uppercase">Documents</div>
                  {results.documents.map((item, idx) => {
                    const globalIdx = allResults.findIndex(r => r._type === 'document' && r.document_id === item.document_id);
                    return (
                      <ResultItem
                        key={item.document_id}
                        item={{ ...item, _type: 'document' }}
                        selected={selectedIndex === globalIdx}
                        onClick={() => handleSelect({ ...item, _type: 'document' })}
                        icon={getResultIcon('document')}
                        title={getResultTitle({ ...item, _type: 'document' })}
                        subtitle={getResultSubtitle({ ...item, _type: 'document' })}
                      />
                    );
                  })}
                </div>
              )}

              {/* Tasks */}
              {results.tasks?.length > 0 && (category === 'all' || category === 'tasks') && (
                <div className="mb-2">
                  <div className="px-4 py-1.5 text-xs font-medium text-muted-foreground uppercase">Tasks</div>
                  {results.tasks.map((item, idx) => {
                    const globalIdx = allResults.findIndex(r => r._type === 'task' && r.task_id === item.task_id);
                    return (
                      <ResultItem
                        key={item.task_id}
                        item={{ ...item, _type: 'task' }}
                        selected={selectedIndex === globalIdx}
                        onClick={() => handleSelect({ ...item, _type: 'task' })}
                        icon={getResultIcon('task')}
                        title={getResultTitle({ ...item, _type: 'task' })}
                        subtitle={getResultSubtitle({ ...item, _type: 'task' })}
                      />
                    );
                  })}
                </div>
              )}

              {/* Invoices */}
              {results.invoices?.length > 0 && (category === 'all' || category === 'invoices') && (
                <div className="mb-2">
                  <div className="px-4 py-1.5 text-xs font-medium text-muted-foreground uppercase">Invoices</div>
                  {results.invoices.map((item, idx) => {
                    const globalIdx = allResults.findIndex(r => r._type === 'invoice' && r.invoice_id === item.invoice_id);
                    return (
                      <ResultItem
                        key={item.invoice_id}
                        item={{ ...item, _type: 'invoice' }}
                        selected={selectedIndex === globalIdx}
                        onClick={() => handleSelect({ ...item, _type: 'invoice' })}
                        icon={getResultIcon('invoice')}
                        title={getResultTitle({ ...item, _type: 'invoice' })}
                        subtitle={getResultSubtitle({ ...item, _type: 'invoice' })}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="border-t px-4 py-2 flex items-center justify-between text-xs text-muted-foreground bg-slate-50">
          <div className="flex items-center gap-3">
            <span><kbd className="px-1.5 py-0.5 bg-white border rounded text-xs">↑↓</kbd> Navigate</span>
            <span><kbd className="px-1.5 py-0.5 bg-white border rounded text-xs">↵</kbd> Select</span>
            <span><kbd className="px-1.5 py-0.5 bg-white border rounded text-xs">esc</kbd> Close</span>
          </div>
          {allResults.length > 0 && (
            <span>{allResults.length} result{allResults.length !== 1 ? 's' : ''}</span>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Result Item Component
function ResultItem({ item, selected, onClick, icon, title, subtitle }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full px-4 py-2.5 flex items-center gap-3 text-left transition-colors",
        selected ? "bg-slate-100" : "hover:bg-slate-50"
      )}
    >
      <div className="flex-shrink-0 h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{title}</p>
        <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
      </div>
      <Badge variant="outline" className="text-xs capitalize">
        {item._type}
      </Badge>
    </button>
  );
}

export default GlobalSearch;
